<script setup>
import { computed, ref } from 'vue';
import XlButton from '../xl/XlButton.vue';
import XlModal from '../xl/XlModal.vue';
import XlTag from '../xl/XlTag.vue';

/**
 * Choosing what leaves the building.
 *
 * The three scopes are not one query with a different limit: "Everything" is
 * the whole hub and only the server can answer it, while the other two are the
 * rows already on screen. That difference is why the column picker is offered
 * for those two and withheld for the first — the server's file has a fixed
 * column set, and letting someone tick columns it would ignore is worse than
 * not offering the choice.
 */
const props = defineProps({
  totalCount: { type: Number, default: 0 },
  filteredCount: { type: Number, default: 0 },
  selectedCount: { type: Number, default: 0 },
  filtersOn: { type: Boolean, default: false },
  columns: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'export']);

const scope = ref(props.selectedCount ? 'selected' : (props.filtersOn ? 'filtered' : 'all'));
const chosen = ref(props.columns.map((column) => column.key));

const scopes = computed(() => [
  { key: 'all', label: 'Everything', hint: 'Everyone you can see', count: props.totalCount },
  { key: 'filtered', label: 'Filtered', hint: 'Just the rows your filters are showing', count: props.filteredCount },
  { key: 'selected', label: 'Selected', hint: "Only the rows you've ticked", count: props.selectedCount },
]);

const picksColumns = computed(() => scope.value !== 'all');
const rowCount = computed(() => scopes.value.find((s) => s.key === scope.value)?.count ?? 0);
const canDownload = computed(() => rowCount.value > 0 && (!picksColumns.value || chosen.value.length > 0));

const toggleColumn = (key) => {
  chosen.value = chosen.value.includes(key)
    ? chosen.value.filter((c) => c !== key)
    : [...chosen.value, key];
};

const run = () => {
  if (!canDownload.value) return;
  emit('export', { scope: scope.value, columns: picksColumns.value ? chosen.value : null });
};
</script>

<template>
  <XlModal title="Export as a list" data-testid="export-modal" @close="emit('close')">
    <p class="export-list__lead">A spreadsheet of your policyholders.</p>

    <p class="export-list__heading">Which rows</p>
    <button
      v-for="option in scopes"
      :key="option.key"
      type="button"
      class="export-list__scope"
      :class="{ 'export-list__scope--on': scope === option.key, 'export-list__scope--empty': !option.count }"
      :aria-pressed="scope === option.key"
      :disabled="!option.count"
      :data-testid="`export-scope-${option.key}`"
      @click="scope = option.key"
    >
      <span class="export-list__scope-body">
        <b>{{ option.label }}</b>
        <span>{{ option.hint }}</span>
      </span>
      <XlTag :tone="option.count ? 'brand' : ''">{{ option.count }}</XlTag>
    </button>

    <template v-if="picksColumns">
      <p class="export-list__heading">
        Which columns
        <button type="button" class="export-list__all" data-testid="export-select-all" @click="chosen = props.columns.map((c) => c.key)">Select all</button>
      </p>
      <div class="export-list__columns">
        <button
          v-for="column in props.columns"
          :key="column.key"
          type="button"
          class="export-list__column"
          :class="{ 'export-list__column--on': chosen.includes(column.key) }"
          :aria-pressed="chosen.includes(column.key)"
          :data-testid="`export-column-${column.key}`"
          @click="toggleColumn(column.key)"
        >{{ chosen.includes(column.key) ? '✓' : '+' }} {{ column.label }}</button>
      </div>
    </template>
    <p v-else class="export-list__note" data-testid="export-columns-fixed">
      The whole-hub file is built on the server and carries its own standard columns.
    </p>

    <p class="export-list__privacy">
      This file holds personal data. Keep it somewhere safe and delete it when you're done.
    </p>

    <template #actions>
      <span class="export-list__count" data-testid="export-summary">
        {{ rowCount }} {{ rowCount === 1 ? 'row' : 'rows' }}<template v-if="picksColumns"> · {{ chosen.length }} {{ chosen.length === 1 ? 'column' : 'columns' }}</template>
      </span>
      <XlButton variant="ghost" @click="emit('close')">Cancel</XlButton>
      <XlButton :disabled="!canDownload" data-testid="export-download" @click="run">Download</XlButton>
    </template>
  </XlModal>
</template>

<style scoped lang="scss">
.export-list__lead {
  margin: 0 0 18px;
  font-size: 13px;
  color: var(--xl-color-outline);
}

.export-list__heading {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin: 18px 0 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--xl-color-border);
  font-size: 13px;
  font-weight: 600;
  color: var(--xl-color-primary-text);
}

.export-list__all {
  margin-left: auto;
  border: 0;
  padding: 0;
  background: none;
  font: inherit;
  font-weight: 400;
  font-size: 12.5px;
  color: var(--xl-color-primary);
  cursor: pointer;
}

.export-list__scope {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-bottom: 8px;
  padding: 12px 14px;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  background: var(--xl-color-surface, #fff);
  font: inherit;
  text-align: left;
  cursor: pointer;

  &--on {
    border-color: var(--xl-color-primary);
    background: var(--xl-color-primary-container);
  }

  &--empty { opacity: 0.5; cursor: not-allowed; }
}

.export-list__scope-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;

  b { font-size: 14.5px; color: var(--xl-color-primary-text); }
  span { font-size: 13px; color: var(--xl-color-outline); }
}

.export-list__columns { display: flex; flex-wrap: wrap; gap: 7px; }

.export-list__column {
  height: 34px;
  padding: 0 13px;
  border: 1px solid var(--xl-color-border);
  border-radius: 999px;
  background: var(--xl-color-surface, #fff);
  font: inherit;
  font-size: 13px;
  color: var(--xl-color-secondary-text);
  cursor: pointer;

  &--on {
    border-color: var(--xl-color-primary);
    background: var(--xl-color-primary-container);
    color: var(--xl-color-on-primary-container);
    font-weight: 600;
  }
}

.export-list__note, .export-list__privacy {
  margin: 12px 0 0;
  padding: 13px 15px;
  border-radius: var(--xl-radius-md);
  background: #fcfafd;
  font-size: 12.5px;
  color: var(--xl-color-secondary-text);
}

.export-list__count {
  flex: 1;
  font-size: 13.5px;
  color: var(--xl-color-outline);
}
</style>
